import { Box, Dialog, DialogContent, DialogContentText, DialogActions, Button, Rating } from "@mui/material";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getReviews } from "../../reduxToolkit/features/customerOnProduct";
import { getAllCustomers } from "../../reduxToolkit/features/auth";
import { PF } from "../../config";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

import ContactReviewer from "../misc/ContactReviewer";
import { useNavigate } from "react-router-dom";
import { useLangPath } from "../../hooks/langAlias";
import { makeUrlFriendly } from "../../hooks/makeUrlFriendly";
import { mergeMedicineList } from "../../hooks/mergeMedicineList";
import { colors, fonts, radii, shadows } from "../../theme/designTokens";

export default function Testimonials({ limit, columns }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lp = useLangPath();

  const [testimonialList, setTestimonialList] = useState([]);
  const [lang, setLang] = useState("en");

  // Dialogs
  const [reviewerDlg, setReviewerDlg] = useState(false);
  const [reviewerBuffer, setReviewerBuffer] = useState(false);
  const [showTestimonial, setShowTestimonial] = useState("");

  // ? Store
  const { reviews } = useSelector((state) => state.customerOnProduct);
  const { customers } = useSelector((state) => state.auth);
  const { medicines } = useSelector((state) => state.medicine);
  const { activeLangFront } = useSelector((state) => state.miscFront);

  const getMedInfo = (medId) => {
    if (medicines && medicines.length > 0) {
      const mergedData = mergeMedicineList(medicines, lang);
      const medInfo = mergedData.find(
        (m) =>
          (lang !== "en" ? m.medicineDetectorId : m.fixedMedicineId) === medId
      );
      if (medInfo) return medInfo;
    }
    return {};
  };

  const getGenericBrand = (id) => {
    let genericBrand = "";
    if (medicines && medicines.length > 0) {
      const M = medicines.find((m) => m.lang === "en");
      if (M) {
        const med = M.data.find((x) => x.fixedMedicineId === id);
        if (med) genericBrand = med.genericBrand;
      }
    }
    return genericBrand;
  };

  // Set Language
  useEffect(() => {
    if (activeLangFront) setLang(activeLangFront.langCode);
  }, [activeLangFront]);

  useEffect(() => {
    if (!(customers && customers.length > 0)) dispatch(getAllCustomers());
    if (!(reviews && reviews.length > 0)) dispatch(getReviews());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reviews && reviews.length > 0) {
      let testimonials = reviews.filter(
        (r) => r.approval && r.rating >= 4 && !r.manualReview
      );
      let unique = [
        ...new Map(
          testimonials.map((item) => [item["medicineId"], item])
        ).values(),
      ];
      let testimonialsCustom = reviews.filter(
        (r) => r.approval && r.manualReview
      );
      setTestimonialList([...testimonialsCustom, ...unique]);
    }
  }, [reviews]);

  const visibleList = limit ? testimonialList.slice(0, limit) : testimonialList;
  const columnCount = columns || 3;

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: columnCount >= 2 ? "repeat(2, 1fr)" : "1fr",
            md: `repeat(${columnCount}, 1fr)`,
          },
          gap: 2.5,
        }}
      >
        {visibleList.map((x, i) => {
          const imgSrc = x.manualReview
            ? x.medicineId
              ? getMedInfo(x.medicineId).image &&
                getMedInfo(x.medicineId).image.length > 0
                ? PF + "adminPanel/medicines/img/" + getMedInfo(x.medicineId).image[0].src
                : PF + "adminPanel/medicines/no-medicine-image.jpg"
              : x.reviewImg && x.reviewImg !== ""
              ? PF + "adminPanel/review/" + x.reviewImg
              : PF + "adminPanel/medicines/no-medicine-image.jpg"
            : getMedInfo(x.medicineId).image && getMedInfo(x.medicineId).image.length > 0
            ? PF + "adminPanel/medicines/img/" + getMedInfo(x.medicineId).image[0].src
            : PF + "adminPanel/medicines/no-medicine-image.jpg";

          return (
            <Box
              key={i}
              sx={{
                borderRadius: radii.lg,
                boxShadow: shadows.card,
                border: `1px solid ${colors.line}`,
                bgcolor: colors.white,
                p: 3,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormatQuoteIcon sx={{ color: colors.primaryLight, fontSize: 30 }} />
              <Rating value={x.rating} precision={0.5} readOnly sx={{ color: "#F5A623", mt: 1 }} />
              <Box
                sx={{
                  fontSize: 14,
                  color: colors.ink2,
                  lineHeight: 1.6,
                  mt: 1.5,
                  mb: 2,
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {x.review}
              </Box>
              <Box
                sx={{
                  mt: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  cursor: x.manualReview ? "default" : "pointer",
                }}
                onClick={() =>
                  !x.manualReview &&
                  navigate(
                    lp(
                      `/our-medicines/${makeUrlFriendly(
                        getGenericBrand(
                          lang !== "en"
                            ? getMedInfo(x.medicineId).medicineDetectorId
                            : getMedInfo(x.medicineId).fixedMedicineId
                        )
                      )}`
                    )
                  )
                }
              >
                <LazyLoadImage
                  alt="reviewed product"
                  effect="blur"
                  src={imgSrc}
                  style={{
                    objectFit: "contain",
                    height: 44,
                    width: 44,
                    borderRadius: radii.md,
                    border: `1px solid ${colors.line}`,
                    background: colors.paperTint,
                  }}
                />
                <Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700, color: colors.ink, fontFamily: fonts.display }}>
                    Verified Customer
                  </Box>
                  {!x.manualReview && (
                    <Box sx={{ fontSize: 12, color: colors.ink3 }}>
                      {getGenericBrand(
                        lang !== "en"
                          ? getMedInfo(x.medicineId).medicineDetectorId
                          : getMedInfo(x.medicineId).fixedMedicineId
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {reviewerDlg && (
        <ContactReviewer
          reviewerDlg={reviewerDlg}
          setReviewerDlg={setReviewerDlg}
          reviewerBuffer={reviewerBuffer}
          setReviewerBuffer={setReviewerBuffer}
          customers={customers || {}}
          lang={lang || "en"}
          genericBrand=""
        />
      )}

      {showTestimonial !== "" && (
        <Dialog
          open={showTestimonial}
          onClose={() => setShowTestimonial("")}
          aria-labelledby={"view-testimonial"}
        >
          <DialogContent>
            <DialogContentText>{showTestimonial}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTestimonial("")} color="error">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
